import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, History } from "lucide-react";
import { cloner, cleanOuter } from "@/lib/clonerApi";
import { apiError } from "@/lib/userApi";

const IDEAS = ["pon el título en rojo", "hazlo responsive para móvil",
  "mejora el SEO de esta página", "cambia los textos a español"];

/** IA del editor (Claude Opus 5.5): cambia el elemento o la página entera. */
export const AiPanel = ({ siteId, el, path, getPageHtml, onResult }) => {
  const [text, setText] = useState("");
  const [scope, setScope] = useState("elemento");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState([]);

  const loadHist = () => cloner.aiHistory(siteId)
    .then(({ data }) => setHist(data.history.filter((h) => h.kind === "edit"))).catch(() => {});
  useEffect(() => { loadHist(); /* eslint-disable-next-line */ }, [siteId]);

  const run = async () => {
    if (!text.trim()) return;
    const useEl = scope === "elemento" && el;
    const code = useEl ? cleanOuter(el) : getPageHtml();
    if (!code) return toast.error("Primero abre una página");
    setBusy(true);
    try {
      const { data } = await cloner.aiEdit(siteId, {
        instruction: text, code, path, language: "html",
      });
      onResult(data.code, useEl ? "elemento" : "pagina");
      setText("");
      loadHist();
      toast.success("Cambio aplicado por la IA");
    } catch (e) { toast.error(apiError(e, "La IA no pudo responder")); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-3" data-testid="ai-panel">
      <div className="flex items-center gap-1.5 mb-2">
        {["elemento", "pagina"].map((s) => (
          <button key={s} data-testid={`ai-scope-${s}`} onClick={() => setScope(s)}
            className={`matrix-chip ${scope === s ? "is-active" : ""} px-3 py-1 rounded-lg text-[10px]`}>
            {s === "elemento" ? "Solo lo elegido" : "Toda la página"}
          </button>
        ))}
      </div>
      <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Dile qué cambiar, por ejemplo: pon el título en rojo" data-testid="ai-input"
        className="w-full glass-card px-2 py-2 bg-black/40 text-white text-[11px] outline-none" />
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {IDEAS.map((i) => (
          <button key={i} onClick={() => setText(i)}
            className="matrix-chip px-2 py-1 rounded text-[10px] text-white/60">{i}</button>
        ))}
      </div>
      <button data-testid="ai-run" onClick={run} disabled={busy}
        className="glass-btn glass-btn-red px-4 py-2 rounded-xl text-[11px] mt-3 flex items-center gap-1.5">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        {busy ? "Pensando…" : "Pedir a la IA"}
      </button>
      <p className="text-[10px] text-white/30 mt-2">
        Usa Claude Opus 5.5. Puedes deshacer con el botón de la barra superior.
      </p>

      {hist.length > 0 && (
        <div className="mt-4" data-testid="ai-history">
          <div className="text-[10px] text-white/40 uppercase tracking-wide mb-1 flex items-center gap-1">
            <History className="w-3 h-3" /> Historial
          </div>
          {hist.slice(0, 10).map((h, i) => (
            <div key={i} className="text-[10px] text-white/50 truncate">· {h.instruction}</div>
          ))}
        </div>
      )}
    </div>
  );
};
